/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FaRegImage } from 'react-icons/fa'

function Card(props) {
  const {
    title,
    chapter,
    chapterTitle,
    parentChapter,
    parentChapterTitle,
    openStaxChapterName,
    openStaxTextbook,
    openStaxParentChapterNumber,
    openStaxParentChapterName,
    openStaxChapterNumber,
    openStaxChapterName: openStaxChildChapterName,
    source,
    sourceTooltip,
    route,
    img,
    href,
    openInNewTab,
    openInAppRoute,
  } = props

  const playRoute = openInAppRoute
    ? openInAppRoute
    : route === '/interference-diffraction'
      ? route
      : `/play/${route}`
  const isExternal = Boolean(href)
  const isOpenStax = Boolean(openInAppRoute)
  const [currentImg, setCurrentImg] = useState(img)
  const [imgError, setImgError] = useState(false)
  const [triedAlt, setTriedAlt] = useState(false)
  const [showSourceTip, setShowSourceTip] = useState(false)
  const [showChapterTip, setShowChapterTip] = useState(false)
  const [openStaxUrl, setOpenStaxUrl] = useState(null)
  const cardRef = useRef(null)
  const sourceInfoRef = useRef(null)
  const chapterPillRef = useRef(null)
  const [sourceTipPos, setSourceTipPos] = useState(null) // {left, top, width}
  const [chapterTipPos, setChapterTipPos] = useState(null)

  const handleImageError = () => {
    if (isOpenStax && !triedAlt) {
      const slug = openInAppRoute ? openInAppRoute.split('/').filter(Boolean).pop() : null
      const altSrc = slug ? `/openstax/${slug}/screenshot.png` : null
      setTriedAlt(true)
      if (altSrc) {
        setCurrentImg(altSrc)
        return
      }
    }
    setImgError(true)
  }

  const handlePillClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleSourceClick = (e) => {
    handlePillClick(e)
    const willShow = !showSourceTip
    setShowSourceTip(willShow)
    setShowChapterTip(false)
    if (willShow && cardRef.current && sourceInfoRef.current) {
      const cardRect = cardRef.current.getBoundingClientRect()
      const iconRect = sourceInfoRef.current.getBoundingClientRect()
      setSourceTipPos({ left: cardRect.left, top: iconRect.bottom + 8, width: cardRect.width })
    }
  }

  const handleChapterClick = (e) => {
    handlePillClick(e)
    const willShow = !showChapterTip
    setShowChapterTip(willShow)
    setShowSourceTip(false)
    if (willShow && cardRef.current && chapterPillRef.current) {
      const cardRect = cardRef.current.getBoundingClientRect()
      const pillRect = chapterPillRef.current.getBoundingClientRect()
      setChapterTipPos({ left: cardRect.left, top: pillRect.bottom + 8, width: cardRect.width })
    }
    if (willShow) {
      if (!openStaxUrl && openStaxTextbook && (openStaxChapterNumber || openStaxChildChapterName)) {
        ; (async () => {
          try {
            let data
            if (openStaxTextbook === 'University Physics Volume 1') {
              data = (await import('../chaptersOpenStaxVol1.json')).default
            } else if (openStaxTextbook === 'University Physics Volume 2') {
              data = (await import('../chaptersOpenStaxVol2.json')).default
            } else if (openStaxTextbook === 'University Physics Volume 3') {
              data = (await import('../chaptersOpenStaxVol3.json')).default
            }
            if (Array.isArray(data)) {
              const match = data.find(
                (c) => c.chapterNumber === String(openStaxChapterNumber) || c.chapterTitle === String(openStaxChildChapterName)
              )
              if (match && match.url) {
                setOpenStaxUrl(match.url)
              }
            }
          } catch (err) {
            // noop; fallback to plain text
          }
        })()
      }
    }
  }

  // Close tooltips when clicking outside of the pills/tooltips
  useEffect(() => {
    const handler = (e) => {
      // if click is inside the card, but not inside either pill container when tooltip open, close tooltips
      const withinSourcePill = sourceInfoRef.current && sourceInfoRef.current.contains(e.target)
      const withinChapterPill = chapterPillRef.current && chapterPillRef.current.contains(e.target)
      if (!withinSourcePill && !withinChapterPill) {
        if (showSourceTip || showChapterTip) {
          setShowSourceTip(false)
          setShowChapterTip(false)
        }
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showSourceTip, showChapterTip])

  // Reposition tooltips on resize/scroll when visible
  useEffect(() => {
    const reposition = () => {
      if (cardRef.current) {
        const cardRect = cardRef.current.getBoundingClientRect()
        if (showSourceTip && sourceInfoRef.current) {
          const iconRect = sourceInfoRef.current.getBoundingClientRect()
          setSourceTipPos({ left: cardRect.left, top: iconRect.bottom + 8, width: cardRect.width })
        }
        if (showChapterTip && chapterPillRef.current) {
          const pillRect = chapterPillRef.current.getBoundingClientRect()
          setChapterTipPos({ left: cardRect.left, top: pillRect.bottom + 8, width: cardRect.width })
        }
      }
    }
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [showSourceTip, showChapterTip])

  const renderSourceTooltipContent = () => {
    if (source === 'serway') {
      return (
        <span>
          A simulation from the textbook <i>Physics for Scientists and Engineers</i> by John W. Jewett and Raymond A. Serway. Ported from Flash via <a className="text-blue-400 underline" href="https://github.com/ruffle-rs/ruffle/" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>Ruffle</a>.
        </span>
      )
    }
    if (source === 'openstax-phet') {
      return (
        <span>
          A simulation from <a className="text-blue-400 underline" href="https://github.com/openstax/simulations" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>OpenStax</a>, based on one from <a className="text-blue-400 underline" href="https://phet.colorado.edu/" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>Phet</a>.
        </span>
      )
    }
    if (sourceTooltip) {
      console.log('sourceTooltip is valid', sourceTooltip)
      return React.isValidElement(sourceTooltip) ? sourceTooltip : <span>{String(sourceTooltip)}</span>
    }
    return null
  }

  const renderChapterTooltipContent = () => {
    const original = (
      <>
        <p className="mb-2">
          <span className="font-semibold">Original chapter from Serway:</span><br />
          {(parentChapter || parentChapterTitle) && (
            <span>{`${parentChapter ? `Ch. ${parentChapter}` : ''}${parentChapterTitle ? ` ${parentChapterTitle}` : ''}`}</span>
          )}
          {(chapter || chapterTitle) && (
            <>
              <br />
              <span>{`${chapter ? `Ch. ${chapter}` : ''}${chapterTitle ? ` ${chapterTitle}` : ''}`}</span>
            </>
          )}
        </p>
        <p className="mb-2">
          <span className="font-semibold">Original filename from Serway:</span><br />
          <span>{`${title}.swf`}</span>
        </p>
      </>
    )
    const openstaxLabel = `Best-match chapter from OpenStax ${openStaxTextbook || ''}:`
    const openstaxLinkLabel = `${openStaxChapterNumber ? `Ch. ${openStaxChapterNumber}` : ''}${openStaxChildChapterName ? ` ${openStaxChildChapterName}` : ''}`
    const openstax = (
      <p className="mb-2">
        <span className="font-semibold">{openstaxLabel}</span><br />
        {(openStaxParentChapterNumber || openStaxParentChapterName) && (
          <span>{`${openStaxParentChapterNumber ? `Ch. ${openStaxParentChapterNumber}` : ''}${openStaxParentChapterName ? ` ${openStaxParentChapterName}` : ''}`}</span>
        )}
        {(openStaxChapterNumber || openStaxChildChapterName) && (
          <>
            <br />
            {openStaxUrl ? (
              <a className="text-blue-400 underline" href={openStaxUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                {openstaxLinkLabel}
              </a>
            ) : (
              <span>{openstaxLinkLabel}</span>
            )}
          </>
        )}
      </p>
    )

    if (source === 'serway') {
      return (
        <span>
          {original}
          {openstax}
        </span>
      )
    }
    return (
      <span>
        {openstax}
      </span>
    )
  }

  const Wrapper = ({ children }) => {
    if (isExternal) {
      return (
        <a href={href} target={openInNewTab ? '_blank' : undefined} rel={openInNewTab ? 'noreferrer' : undefined}>
          {children}
        </a>
      )
    }
    return <Link to={playRoute}>{children}</Link>
  }

  // Removed action button: clicking the card already navigates

  return (
    <div ref={cardRef} className="py-2 px-3 w-full sm:w-[315px] bg-gray-50 rounded-lg border border-gray-100 dark:bg-[#1e293b] dark:border-[#1e293b]">
      <Wrapper>
        <h5 className="mb-1 text-lg/6 font-normal tracking-tight text-gray-900 dark:text-white">
          {isOpenStax ? (
            <span>{title}</span>
          ) : chapterTitle ? (
            <span>{chapterTitle}</span>
          ) : parentChapterTitle ? (
            <span>{parentChapterTitle}</span>
          ) : (
            <span></span>
          )}
        </h5>
        {/* underline removed */}
        {img && !imgError ? (
          <img src={currentImg || img} onError={handleImageError} width={'100%'} className="rounded-md" />
        ) : (
          <div className=" w-full h-40 bg-gradient-to-br from-gray-200 to-gray-100 flex items-center justify-center rounded dark:from-slate-700 dark:to-slate-500">
            <FaRegImage className="text-4xl text-gray-400 dark:text-gray-300" />
          </div>
        )}
      </Wrapper>

      <div className="mt-2 flex flex-wrap items-start gap-x-3 gap-y-2">
        {source && (
          <div className="text-xs text-gray-700 dark:text-white flex items-center gap-2 select-none">
            <span className="font-bold">Source:</span>
            <button
              ref={sourceInfoRef}
              className="text-left text-gray-600 hover:text-gray-900 underline decoration-dashed underline-offset-2 dark:text-gray-300 dark:hover:text-white"
              aria-label="Source info"
              onClick={handleSourceClick}
            >
              {(() => {
                if (source === 'openstax-phet') {
                  return 'OpenStax (Phet)'
                }
                const s = String(source || '').replace(/[-_]+/g, ' ').toLowerCase()
                return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
              })()}
            </button>
          </div>
        )}
        {openStaxChapterName && (
          <div className="text-xs text-gray-700 dark:text-white flex items-center gap-2 select-none">
            <span className="font-bold">Topic:</span>
            <button
              ref={chapterPillRef}
              className="text-left text-gray-600 hover:text-gray-900 underline decoration-dashed underline-offset-2 dark:text-gray-300 dark:hover:text-white"
              aria-label="Topic info"
              onClick={handleChapterClick}
            >
              {(() => {
                const s = String(openStaxChapterName || '').replace(/[-_]+/g, ' ').trim()
                return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''
              })()}
            </button>
          </div>
        )}
      </div>

      {/* Fixed-position tooltips spanning card width */}
      {showSourceTip && sourceTipPos && (
        <div
          className="fixed z-50 bg-white text-gray-900 text-xs p-3 rounded shadow-xl normal-case border border-gray-200 dark:bg-gray-900 dark:text-gray-100 dark:border-slate-700"
          style={{ left: sourceTipPos.left, top: sourceTipPos.top, width: sourceTipPos.width }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {renderSourceTooltipContent()}
        </div>
      )}
      {showChapterTip && chapterTipPos && (
        <div
          className="fixed z-50 bg-white text-gray-900 text-xs p-3 rounded shadow-xl normal-case border border-gray-200 dark:bg-gray-900 dark:text-gray-100 dark:border-slate-700"
          style={{ left: chapterTipPos.left, top: chapterTipPos.top, width: chapterTipPos.width }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {renderChapterTooltipContent()}
        </div>
      )}
    </div>
  )
}

export default Card
